import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Stack,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Person,
  CalendarToday,
  Schedule,
  Flag,
  Comment,
  AttachFile,
  CheckCircle,
  Cancel,
  Refresh,
} from '@mui/icons-material';
import { useCrud, useModal, useForm } from '../hooks';
import { ocorrenciaService, eventoService, ocorrenciaTipoService } from '../services/api';

const OcorrenciasKanban = ({ eventoId = null }) => {
  const { data: eventos } = useCrud(eventoService, 'eventos');
  const { data: tipos } = useCrud(ocorrenciaTipoService, 'tipos de ocorrências');
  const [selectedEvento, setSelectedEvento] = useState(eventoId || '');
  const [kanbanData, setKanbanData] = useState({
    'To do': [],
    'In progress': [],
    'Ready to test': [],
    'Done': []
  });
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  const validationRules = {
    evento_id: { required: true, label: 'Evento' },
    data_abertura: { required: true, label: 'Data de Abertura' },
    status: { required: false, label: 'Status' },
    descricao: { required: false, label: 'Descrição' },
    nome_solicitante: { required: false, label: 'Nome do Solicitante' },
    tipo_id: { required: false, label: 'Tipo' },
    observacoes: { required: false, label: 'Observações' }
  };

  const form = useForm(
    {
      evento_id: '',
      data_abertura: new Date().toISOString().split('T')[0],
      status: 'To do',
      descricao: '',
      nome_solicitante: '',
      tipo_id: '',
      observacoes: ''
    },
    validationRules
  );

  useEffect(() => {
    if (selectedEvento) {
      loadKanbanData();
    }
  }, [selectedEvento]);

  const loadKanbanData = async () => {
    try {
      setLoading(true);
      
      // Dados mock para demonstração
      const mockData = {
        'To do': [
          {
            id: 'task-1',
            descricao: 'Design New Marketing Campaign',
            assignees: [
              { name: 'João Silva', color: '#ff6b6b' },
              { name: 'Maria Santos', color: '#4ecdc4' }
            ],
            priority: 'high',
            tags: ['Frontend', 'Design'],
            comments: 4,
            attachments: 2
          },
          {
            id: 'task-2',
            descricao: 'Analyze Customer Feedback',
            assignees: [
              { name: 'Pedro Costa', color: '#45b7d1' }
            ],
            priority: 'medium',
            tags: ['Backend'],
            comments: 2,
            attachments: 1
          },
          {
            id: 'task-3',
            descricao: 'Update Website Content',
            assignees: [
              { name: 'Ana Lima', color: '#96ceb4' },
              { name: 'Carlos Silva', color: '#feca57' }
            ],
            priority: 'low',
            tags: ['UI/UX'],
            comments: 3,
            attachments: 0
          }
        ],
        'In progress': [
          {
            id: 'task-4',
            descricao: 'Conduct Market Research',
            assignees: [
              { name: 'Maria Santos', color: '#4ecdc4' }
            ],
            priority: 'medium',
            tags: ['Feature'],
            comments: 5,
            attachments: 3
          },
          {
            id: 'task-5',
            descricao: 'Develop Software Application',
            assignees: [
              { name: 'João Silva', color: '#ff6b6b' },
              { name: 'Pedro Costa', color: '#45b7d1' },
              { name: 'Ana Lima', color: '#96ceb4' }
            ],
            priority: 'high',
            tags: ['Backend', 'Frontend'],
            comments: 8,
            attachments: 5
          }
        ],
        'Ready to test': [],
        'Done': [
          {
            id: 'task-6',
            descricao: 'Organize Team Meeting',
            assignees: [
              { name: 'Carlos Silva', color: '#feca57' }
            ],
            priority: 'low',
            tags: ['Meeting'],
            comments: 1,
            attachments: 0
          }
        ]
      };

      setKanbanData(mockData);
    } catch (error) {
      console.error('Erro ao carregar dados do kanban:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getColumnColor = (columnId) => {
    switch (columnId) {
      case 'To do': return '#64748b';
      case 'In progress': return '#3b82f6';
      case 'Ready to test': return '#f59e0b';
      case 'Done': return '#10b981';
      default: return '#64748b';
    }
  };

  const getColumnCount = (columnId) => {
    return kanbanData[columnId]?.length || 0;
  };

  const handleAddCard = (columnId) => {
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      // Aqui você salvaria no backend
      setOpenDialog(false);
      form.reset();
    } catch (error) {
      console.error('Erro ao criar ocorrência:', error);
    }
  };

  const moveCard = (cardId, fromColumn, toColumn) => {
    const newKanbanData = { ...kanbanData };
    const cardIndex = newKanbanData[fromColumn].findIndex(card => card.id === cardId);
    
    if (cardIndex !== -1) {
      const card = newKanbanData[fromColumn][cardIndex];
      newKanbanData[fromColumn].splice(cardIndex, 1);
      newKanbanData[toColumn].push(card);
      setKanbanData(newKanbanData);
    }
  };

  const KanbanCard = ({ item }) => (
    <Card
      sx={{
        mb: 2,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
        },
      }}
      onClick={() => setSelectedCard(item)}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header with priority and menu */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Chip
            size="small"
            label={item.priority?.toUpperCase()}
            color={getPriorityColor(item.priority)}
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
          <IconButton size="small" sx={{ p: 0.5 }}>
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>

        {/* Title */}
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {item.descricao || 'Sem descrição'}
        </Typography>

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <Stack direction="row" spacing={0.5} mb={2} flexWrap="wrap">
            {item.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 18 }}
              />
            ))}
          </Stack>
        )}

        {/* Footer with stats and assignees */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Box display="flex" alignItems="center">
              <Comment fontSize="small" sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                {item.comments || 0}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <AttachFile fontSize="small" sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                {item.attachments || 0}
              </Typography>
            </Box>
          </Stack>

          {/* Assignees */}
          <Stack direction="row" spacing={-1}>
            {item.assignees?.slice(0, 3).map((assignee, index) => (
              <Tooltip key={index} title={assignee.name}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: '0.7rem',
                    bgcolor: assignee.color,
                    border: '2px solid white',
                  }}
                >
                  {assignee.name.charAt(0)}
                </Avatar>
              </Tooltip>
            ))}
            {item.assignees?.length > 3 && (
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: '0.6rem',
                  bgcolor: 'grey.400',
                  border: '2px solid white',
                }}
              >
                +{item.assignees.length - 3}
              </Avatar>
            )}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );

  const KanbanColumn = ({ columnId, title, items }) => (
    <Paper
      elevation={1}
      sx={{
        width: 300,
        bgcolor: 'grey.50',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Column Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: 'white',
          borderBottom: `3px solid ${getColumnColor(columnId)}`,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
            <Chip
              label={getColumnCount(columnId)}
              size="small"
              sx={{
                bgcolor: getColumnColor(columnId),
                color: 'white',
                fontWeight: 600,
                minWidth: 24,
                height: 20,
              }}
            />
          </Box>
          <IconButton
            size="small"
            onClick={() => handleAddCard(columnId)}
            sx={{ color: getColumnColor(columnId) }}
          >
            <Add />
          </IconButton>
        </Box>
      </Box>

      {/* Column Content */}
      <Box
        sx={{
          p: 2,
          minHeight: 400,
        }}
      >
        {items.map((item) => (
          <KanbanCard key={item.id} item={item} />
        ))}
        
        {items.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2">
              Nenhum item nesta coluna
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Kanban
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={loadKanbanData}
          variant="outlined"
          size="small"
        >
          Atualizar
        </Button>
      </Box>

      {/* Event Selector */}
      {!eventoId && (
        <FormControl fullWidth sx={{ mb: 3, maxWidth: 300 }}>
          <InputLabel>Selecionar Evento</InputLabel>
          <Select
            value={selectedEvento}
            onChange={(e) => setSelectedEvento(e.target.value)}
            label="Selecionar Evento"
          >
            {eventos?.map((evento) => (
              <MenuItem key={evento.id} value={evento.id}>
                {evento.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Kanban Board */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          overflowX: 'auto',
          pb: 2,
          minHeight: 500,
        }}
      >
        {Object.entries(kanbanData).map(([columnId, items]) => (
          <KanbanColumn
            key={columnId}
            columnId={columnId}
            title={columnId}
            items={items}
          />
        ))}
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Ocorrência</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={form.data.descricao}
                onChange={(e) => form.setField('descricao', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Solicitante"
                value={form.data.nome_solicitante}
                onChange={(e) => form.setField('nome_solicitante', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Data de Abertura"
                value={form.data.data_abertura}
                onChange={(e) => form.setField('data_abertura', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={form.data.tipo_id}
                  onChange={(e) => form.setField('tipo_id', e.target.value)}
                  label="Tipo"
                >
                  {tipos?.map((tipo) => (
                    <MenuItem key={tipo.id} value={tipo.id}>
                      {tipo.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                value={form.data.observacoes}
                onChange={(e) => form.setField('observacoes', e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OcorrenciasKanban;